/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: remove explicit any - WEB-10980

import ReactGA from 'react-ga';
import { Device, DeviceInfoService, UserDeviceProps, TrackEventService } from 'us-web-services';
import { Environment } from '../../dependencies';
import type { ConfigService } from '../ConfigService';

export enum TrackerTypes {
  /** Google Analytics */
  GA = 'GA',
  /** Backend API (PostHog, Mixpanel, Firebase) */
  API = 'API',
}

export const defaultTrackerConfig = {
  services: [TrackerTypes.API],
} as {
  services: (TrackerTypes.GA | TrackerTypes.API)[];
};

export class TrackerService {
  #config: ConfigService;
  #deviceInfo: Device;
  #env: Environment;

  constructor(config: ConfigService, environment: Environment) {
    this.#config = config;
    this.#env = environment;

    if (this.#config.GOOGLE_APP_ID) {
      ReactGA.initialize(this.#config.GOOGLE_APP_ID);
    }

    this.#deviceInfo = DeviceInfoService.getDeviceInfo();
  }

  getSuperProps(): UserDeviceProps {
    const { user } = this.#env.stores.user;

    const baseProps = user ? {
      active: user.active,
      ...this.getDeviceProps(),
      } : this.getDeviceProps();

    return {
      properties: baseProps,
    };
  }

  getDeviceProps(): Device {
    const device = {
      client: this.#deviceInfo.client,
      deviceType: this.#deviceInfo.deviceType,
      deviceModel: this.#deviceInfo.deviceModel,
      deviceVendor: this.#deviceInfo.deviceVendor,
      osName: this.#deviceInfo.osName,
      osVersion: this.#deviceInfo.osVersion,
      screenSize: this.#deviceInfo.screenSize,
      userAgent: this.#deviceInfo.userAgent,
      browser: this.#deviceInfo.browser,
      browserVersion: this.#deviceInfo.browserVersion,
      isMobile: this.#deviceInfo.isMobile,
      isTablet: this.#deviceInfo.isTablet,
      isDesktop: this.#deviceInfo.isDesktop,
    };

    return device;
  }

  async track(event: string, params?: any, category?: string, config: any = defaultTrackerConfig): Promise<void> {
    // order matters for overrides
    const superProps = this.getSuperProps();

    const properties = {
      url: window.location.href,
      ...superProps.properties,
      ...params,
    };

    if (config.services.includes(TrackerTypes.GA)) {
      ReactGA.event({
        category: category ?? '',
        action: event,
      });
    }

    if (config.services.includes(TrackerTypes.API)) {
      const { user } = this.#env.stores.user;

      try {
        await TrackEventService.trackEvent({
          name: event,
          properties,
          id: user?.id?.toString() || undefined,
        });
      } catch (e) {
        console.warn(e);
      }
    }
  }

  // Push a GTM event to window.dataLayer. Mirrors Angular's
  // AnalyticsService.triggerGTMEvent — required for conversion pixels
  // (Facebook/Google Ads/LinkedIn) on signup, paywall, etc.
  triggerGTMEvent(payload: Record<string, unknown> & { event: string }): void {
    if (typeof window === 'undefined') return;
    const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
    if (!w.dataLayer) w.dataLayer = [];
    w.dataLayer.push(payload);
  }

  // GA pageview on every SPA route change. Mirrors Angular's
  // AnalyticsService.trackGaPageView (apps/angular/src/js/apps/app.js:1723) —
  // without this, GA session/funnel reports break on the React app.
  pageview(path: string): void {
    if (!this.#config.GOOGLE_APP_ID) return;
    ReactGA.pageview(path);
  }

  async trackEvent(event: string, params?: any, category?: string, config: any = defaultTrackerConfig): Promise<void> {
    // order matters for overrides
    const superProps = this.getSuperProps();

    const properties = {
      ...superProps.properties,
      ...params,
    };

    if (config.services.includes(TrackerTypes.GA)) {
      ReactGA.event({
        category: category ?? '',
        action: event,
      });
    }

    if (config.services.includes(TrackerTypes.API)) {
      const { user } = this.#env.stores.user;

      try {
        await TrackEventService.trackUserEvent({
          properties,
          id: user?.id?.toString() || undefined,
        });
      } catch (e) {
        console.warn(e);
      }
    }
  }
}
